import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import Visibility from '../../../svg/icons/Visibility';
import VisibilityOff from '../../../svg/icons/VisibilityOff';
import RadioOverlay from '../../components/RadioOverlay/RadioOverlay';
import FieldTypes from '../../enums/FieldTypes';
import MessageType from '../../enums/MessageType';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import useMaskValue from '../../hooks/useMaskValue';
import DefineFieldPropType from '../../propTypes/DefineField.proptype';
import FieldContainerPropType from '../../propTypes/FieldContainer.proptype';
import AutocompleteField from '../../UI/AutocompleteField/AutocompleteField';
import AutocompleteFieldWithBetterClickHandling
from '../../UI/AutocompleteField/AutocompleteFieldWithBetterClickHandling';
import Button from '../../UI/Button/Button';
import Checkbox from '../../UI/Checkbox/Checkbox';
import FieldIcon from '../../UI/FieldIcon/FieldIcon';
import {InputMaskInput} from '../../UI/InputMaskInput/InputMaskInput';
import Message from '../components/ui/Message/Message';
import MockInput from '../components/ui/MockInput/MockInput';
import CircularCrossIcon from '../components/ui/svg/Components/CircularCrossIcon';
import Translation from '../lang/Translation';
import getExperimentCollapsedOptionalFields from '../selectors/getExperimentCollapsedOptionalFields';
import isUsingArtemisStyles from '../selectors/isUsingArtemisStyles';
import {getSettings, getSiteCode} from '../selectors/selectors';
import {isMobile} from '../selectors/DeviceSelectors';
import isRTL from '../utils/isRTL';
import Field2 from './Field2';
import FieldHelpLabel from './FieldHelpLabel/FieldHelpLabel';
import FieldWrapper from './FieldWrapper/FieldWrapper';
import useStyles from './InputField.styles';

export function InputField(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme, isRightToLeft: isRTL()});
  const [isVisible, setIsVisible] = useState(false);
  const [isFieldCollapsed, setIsFieldCollapsed] = useState(props.shouldHideCollapsedOptionalFields);
  const [isFocused, setIsFocused] = useState(false);
  const [showClearInput, setShowClearInput] = useState(false);
  const hasBeenChangedRef = useRef(false);
  const {maskValue, handleMaskChange} = useMaskValue();
  const inputFieldRef = useRef(null);
  
  useDidUpdateEffect(() => {
    if (props.shouldHideCollapsedOptionalFields && inputFieldRef.current && !isFieldCollapsed) {
      inputFieldRef.current.focus();
    }
  }, [isFieldCollapsed, inputFieldRef.current]);
  
  
  const getUniqueTypeProps = () => {
    if (props.definedField.type === 'radio') {
      return {
        value: props.definedField.value,
        checked: props.value === props.definedField.value,
      };
    }
    
    if (props.definedField.type === 'checkbox') {
      return {
        value: props.value,
        checked: props.value === props.definedField.value,
      };
    }
    
    return {
      value: props.value || '',
      placeholder: (props.definedField.placeholder && Translation.getText(props.definedField.placeholder))
        || '',
    };
  };
  
  const toggleVisibility = () => setIsVisible(!isVisible);
  
  const handleBlur = onBlur => (event) => {
    setIsFocused(false);
    onBlur(event, hasBeenChangedRef.current);
    if (props.clearInput) {
      setShowClearInput(false);
    }
  };
  
  const handleFocus = onFocus => (event) => {
    setIsFocused(true);
    props.handleFocus();
    onFocus(event);
    if (props.clearInput) {
      setShowClearInput(true);
    }
  };
  
  const handleChange = onChange => (event) => {
    handleMaskChange(event);
    
    if (props.handleSlash) {
      event?.preventDefault();
      props.handleSlash(event);
    }
    if (props.handleChange) {
      props.handleChange();
    } else {
      onChange(event);
      hasBeenChangedRef.current = true;
    }
  };
  
  const nameOnForm = props.fieldNamePrefix
    ? `${props.fieldNamePrefix}.${props.definedField.name}`
    : '';
  
  const defaultNames = {
    nameOnForm,
    id: `InputField--${nameOnForm || props.definedField.name}`,
    ...props.legacyNames,
  };
  
  let cardTypeName;
  if (props.iconName) {
    cardTypeName = props.iconName;
    if (props.iconName === 'visa-credit' || 'visa-debit') {
      cardTypeName = 'visa';
    }
    if (props.iconName === 'mastercard') {
      cardTypeName = 'mc';
    }
  }
  
  const getIcons = () => (
    <div className={classes.iconWrapper} data-test="InputField__icon-wrapper">
      {props.icon && (
        <FieldIcon icon={props.icon} altKey={`checkout.payment.method.${cardTypeName}.name`} />
      )}
      
      {props.definedField.type === 'password' && (
        <Button
          className={classes.showPasswordIcon}
          noResize
          textOnly
          id="showPasswordIcon"
          icon={isVisible ? <Visibility /> : <VisibilityOff />}
          onClick={toggleVisibility}
        />
      )}
      
      {(props.clearInput && props.value !== '') && showClearInput && (
        <Button
          className={classes.clearInput}
          noResize
          textOnly
          id="clearInput"
          icon={<CircularCrossIcon id="clearInput" />}
          onMouseDown={props.clearInput}
        />
      )}
    </div>
  );
  
  const getType = () => {
    if (props.definedField.type) {
      if (props.definedField.type === 'password' && isVisible) {
        return 'text';
      }
      return props.definedField.type;
    }
    return 'text';
  };
  
  const getIsInputFieldInCollapsedState = () => {
    if (!props.shouldHideCollapsedOptionalFields) {
      return false
    }
    
    if (props.definedField.shouldNotCollapse) {
      return false
    }
    
    if (props.definedField.required) {
      return false
    }
    
    if (props.definedField?.type === 'checkbox' || props.fieldType === FieldTypes.MOCK) {
      return false
    }
    
    if (props.value !== '' && props.value !== undefined && props.value !== null) {
      return false
    }
    
    return isFieldCollapsed;
  }
  
  const shouldShowHelper = () => {
    return !getIsInputFieldInCollapsedState()
  }
  
  const getIsLabelHidden = () => {
    if(props.hideLabel){
      return true
    }
    
    return getIsInputFieldInCollapsedState();
  }
  
  const getInput = (errorText, onChange, onBlur, onFocus) => {
    const className = `${defaultNames.nameInClass} ${errorText ? 'error-block-v2' : ''} ${classes.inputField}`;
  
    if (getIsInputFieldInCollapsedState()) {
      return (
        <Button link id="input-field-optional" data-test="InputField__optionalField" className={classes.labelOptional} onClick={() => setIsFieldCollapsed(false)}>
          <Translation optionalExtension={props.siteCode}>delivery.address.label.add</Translation>
          <Translation optionalExtension={props.siteCode}>{props.definedField.label}</Translation>
          <Translation optionalExtension={props.siteCode}>delivery.address.label.optional</Translation>
        </Button>
      );
    }
    
    if (props.fieldType === FieldTypes.AUTOCOMPLETE) {
      return props.shouldUseNewAutoCompleteField ? (
        <AutocompleteFieldWithBetterClickHandling
          isCollection={props.isCollection}
          isOverlay={props.isOverlay}
          shouldUseNewCollectionOverlay={props.shouldUseNewCollectionOverlay}
          id={defaultNames.id}
          className={className}
          onSelect={props.onSelect}
          value={props.value}
          disabled={props.disabled}
          predictions={props.predictions}
          onChange={handleChange(onChange)}
          onBlur={handleBlur(onBlur)}
          onClick={props.onClick}
          handleFocus={handleFocus(onFocus)}
          placeholder={props.placeholder}
          predictionComponent={props.predictionComponent}
          emptyPredictionsFallback={props.emptyPredictionsFallback}
          showCurrentLocation={props.showCurrentLocation}
          type={props.definedField.type}
          autoComplete={props.definedField.autoComplete}
          nameOnForm={defaultNames?.nameOnForm}
          fieldCollapsed={getIsInputFieldInCollapsedState()}
        />
      ) : (
        <AutocompleteField
          isCollection={props.isCollection}
          isOverlay={props.isOverlay}
          shouldUseNewCollectionOverlay={props.shouldUseNewCollectionOverlay}
          id={defaultNames.id}
          className={className}
          onSelect={props.onSelect}
          value={props.value}
          disabled={props.disabled}
          predictions={props.predictions}
          onChange={handleChange(onChange)}
          onBlur={handleBlur(onBlur)}
          onClick={props.onClick}
          handleFocus={handleFocus(onFocus)}
          placeholder={props.placeholder}
          predictionComponent={props.predictionComponent}
          emptyPredictionsFallback={props.emptyPredictionsFallback}
          showCurrentLocation={props.showCurrentLocation}
          type={props.definedField.type}
          autoComplete={props.definedField.autoComplete}
          nameOnForm={defaultNames?.nameOnForm}
          fieldCollapsed={getIsInputFieldInCollapsedState()}
        />
      );
    }
    
    if (props.definedField.type === 'checkbox') {
      return (
        <Checkbox
          {...getUniqueTypeProps()}
          id={defaultNames.id}
          handleChange={handleChange(onChange)}
        />
      );
    }
    
    if (props.fieldType === FieldTypes.MOCK) {
      return (
        <MockInput
          id={defaultNames.id}
          dataTestSuffix={defaultNames.id}
          displayValue={props.value}
          inputValue={props.value}
          errorText={errorText}
          validate={onBlur}
        />
      );
    }
  
    if (props.definedField.type === 'mask') {
      return (
        <InputMaskInput
          onBlur={handleBlur(onBlur)}
          onFocus={handleFocus(onFocus)}
          onChange={handleChange(onChange)}
          onKeyPress={props.onKeyPress}
          value={maskValue}
          id={defaultNames.id}
          name={defaultNames.nameOnForm}
          placeholder={props.placeholder}
          className={className}
          type={getType()}
          aria-required={props.definedField.required}
          aria-describedby={props?.definedField?.helperText && `FieldHelpLabel--${defaultNames.id}`}
          aria-controls={props.ariaControls}
          aria-activedescendant={props.ariaActivedescendant}
          aria-hidden={getIsInputFieldInCollapsedState()}
        />
      );
    }
    
    return (
      <input
        {...getUniqueTypeProps()}
        data-testid="input-field"
        type={getType()}
        className={className}
        disabled={props.disabled}
        onChange={handleChange(onChange)}
        onBlur={handleBlur(onBlur)}
        onFocus={handleFocus(onFocus)}
        onKeyUp={props.onKeyUp}
        onKeyDown={event => props.onKeyDown(onChange, event)}
        onKeyPress={props.onKeyPress}
        id={defaultNames.id}
        name={defaultNames.nameOnForm}
        autoFocus={props.definedField.autoFocus}
        autoComplete={props.autoCompleteState || props.definedField.autoComplete}
        required={props.definedField.required}
        aria-required={props.definedField.required}
        aria-describedby={props?.definedField?.helperText && `FieldHelpLabel--${defaultNames.id}`}
        aria-controls={props.ariaControls}
        aria-activedescendant={props.ariaActivedescendant}
        aria-hidden={getIsInputFieldInCollapsedState()}
        inputMode= {props.inputMode}
        pattern={props.pattern}
        ref={inputFieldRef}
      />
    );
  
    
  };
  return (
    <Field2
      fieldContainer={props.fieldContainer}
      value={props.value}
      definedField={props.definedField}
      validationCallback={props.validationCallback}
      onBlur={props.onBlur}
      onChange={props.onChange}
      disabled={props.disabled}
      nameOnForm={defaultNames.nameOnForm}
      addressType={props.addressType}
    >
      {(onChange, onBlur, onFocus, errorText) => (
        <FieldWrapper
          label={props.definedField.label}
          required={props.definedField.required}
          customContainerId={props.definedField.containerId}
          link={props.definedField.link}
          toolTip={props.definedField.toolTip}
          htmlFor={defaultNames.id}
          wrappedClassName={defaultNames.wrappedClassName}
          hideLabel={getIsLabelHidden()}
          errorText={errorText}
          value={props.value || ''}
          maxLength={props.definedField.maxLength}
          fixedLength={props.definedField.fixedLength}
          validationCleaner={props.definedField.validationCleaner}
          fieldType={props.definedField.type}
          fieldWidth={props.definedField.width}
          shouldNotShowFieldOptionalLabel={props.definedField.shouldNotCollapse}
        >
          <div className={classes.inputContainer}>
            {getInput(errorText, onChange, onBlur, onFocus)}
            {props.definedField.type === 'radio' && (
              <RadioOverlay
                isSelected={props.value === props.definedField.value}
                isFocused={isFocused}
                testName={defaultNames.id}
              />
            )}
            {getIcons()}
          </div>
          {props.definedField.helperText && shouldShowHelper() && (
            <FieldHelpLabel id={defaultNames.id}>
              {props.definedField.helperText}
            </FieldHelpLabel>
          )}
          {props.definedField.infoMessage && (
            <div className={classes.infoMessage}>
              <Message type={MessageType.INFO}>
                <Translation optionalExtension={props.siteCode}>
                  ${props.definedField.message}
                </Translation>
              </Message>
            </div>
          )}
        </FieldWrapper>
      )}
    </Field2>
  );
}

InputField.defaultProps = {
  disabled: false,
  predictions: [],
  fieldType: FieldTypes.INPUT,
  emptyPredictionsFallback: null,
  onBlur: () => {
  },
  onSelect: () => {
  },
  onChange: () => {
  },
  onKeyDown: () => {
  },
  onKeyUp: () => {
  },
  onKeyPress: () => {
  },
  handleFocus: () => {
  },
  isCollection: false,
};

InputField.propTypes = {
  isCollection: PropTypes.bool,
  shouldUseNewCollectionOverlay: PropTypes.bool.isRequired,
  isOverlay: PropTypes.bool,
  legacyNames: PropTypes.shape({
    wrappedClassName: PropTypes.string,
  }).isRequired,
  definedField: PropTypes.shape(DefineFieldPropType).isRequired,
  customContainerId: PropTypes.string,
  fieldContainer: PropTypes.shape(FieldContainerPropType).isRequired,
  predictionComponent: PropTypes.func,
  emptyPredictionsFallback: PropTypes.node,
  validationCallback: PropTypes.objectOf(PropTypes.func),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  tabIndex: PropTypes.number,
  disabled: PropTypes.bool,
  fieldNamePrefix: PropTypes.string,
  placeholder: PropTypes.string,
  ariaAutocomplete: PropTypes.string,
  ariaActivedescendant: PropTypes.string,
  ariaControls: PropTypes.string,
  autoCompleteState: PropTypes.string,
  predictions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  fieldType: PropTypes.symbol,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyUp: PropTypes.func,
  onSelect: PropTypes.func,
  handleFocus: PropTypes.func,
  icon: PropTypes.node,
  iconName: PropTypes.string,
  clearInput: PropTypes.func,
  handleSearch: PropTypes.func,
  hideLabel: PropTypes.bool,
  showCurrentLocation: PropTypes.bool,
  handleChange: PropTypes.func,
  handleSlash: PropTypes.func,
  isUsingArtemisStyles: PropTypes.bool,
  shouldUseNewAutoCompleteField: PropTypes.bool,
  addressType: PropTypes.oneOf(['delivery', 'billing']),
  inputMode: PropTypes.oneOf([ "none" , "text" , "tel" , "url" , "email" , "numeric" , "decimal" , "search" , undefined]),
  pattern: PropTypes.string,
  shouldHideCollapsedOptionalFields: PropTypes.bool,
  isMobile: PropTypes.bool,
  siteCode: PropTypes.string,
};

const mapStateToProps = state => ({
  isUsingArtemisStyles: isUsingArtemisStyles(state),
  shouldHideCollapsedOptionalFields: getExperimentCollapsedOptionalFields(state),
  shouldUseNewAutoCompleteField: !!getSettings(state)?.should_use_new_autocomplete_field,
  isMobile: isMobile(state),
  siteCode: getSiteCode(state),
});

export default connect(mapStateToProps)(InputField);
